import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Settings as SettingsIcon, Package, Info, Users } from "lucide-react";
import { fetchParentHierarchy, fetchUserData } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";
import {
  fetchUsersSearchSignageTeamMembers,
  fetchNotificationEmailsSettingByKey,
  patchGeneralNotificationEmails,
  SALES_ORDER_NOTIFICATION_EMAILS_KEY,
  INSTALLATION_ORDER_NOTIFICATION_EMAILS_KEY,
  REMOVAL_ORDER_NOTIFICATION_EMAILS_KEY,
  postProjectSettingsQuoteAssign,
  type ProjectSettingsQuoteAssignPayload,
  SignageDesignerSelectOption,
} from "components/proejctsandquoteapicalls";

const API_BASE = process.env.REACT_APP_API_URL || "";
const SETTINGS_URL = `${API_BASE}/settings/general`;
const PROJECT_SETTINGS_URL = `${API_BASE}/project-settings`;
const SHIPPING_HANDLING_CHARGES_KEY = "shipping_handling_charges";
const SHIPPING_HANDLING_CHARGES_DESCRIPTION = "Shipping handling charges";

const QUOTE_ASSIGNEE_SEARCH_PAGE_SIZE = 10;
type NotifyAgentOption = { value: string; label: string; email?: string; licenseType?: string };
type NotificationEmailEntry = { value: string; license_type: string };
const NO_LICENSE = "NO_LICENSE";

const OrderJobNotifyDropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

const orderJobNotifyMultiStyles = (hasError: boolean) => ({
  container: (base: any, state: any) => ({
    ...base,
    cursor: state.isDisabled ? "not-allowed" : "default",
    pointerEvents: state.isDisabled ? "auto" : base.pointerEvents,
  }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 39,
    height: "auto",
    borderRadius: 10,
    alignItems: "center",
    backgroundColor: state.isDisabled ? "#f2f2f2" : state.isFocused ? "#fafafa" : "#f5f6fa",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: hasError ? "#747981" : state.isFocused ? "#9ca3af" : "#c9ced6",
    boxShadow: "none",
    outline: "none",
    transition: "all .2s ease-in-out",
    cursor: state.isDisabled ? "not-allowed" : "default",
    ":hover": state.isDisabled
      ? {}
      : {
          borderColor: hasError ? "#747981" : "#9ca3af",
          backgroundColor: "#fafafa",
        },
  }),
  valueContainer: (base: any, state: any) => ({
    ...base,
    padding: "4px 12px",
    minHeight: 39,
    display: "flex",
    alignItems: "center",
    cursor: state.isDisabled ? "not-allowed" : base.cursor,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    paddingRight: 10,
    minHeight: 39,
    display: "flex",
    alignItems: "center",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (base: any, state: any) => ({
    ...base,
    color: "#6b7280",
    margin: 0,
    display: "flex",
    alignItems: "center",
    cursor: state.isDisabled ? "not-allowed" : base.cursor,
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  menu: (base: any) => ({ ...base, borderRadius: 10, overflow: "hidden" }),
});

const orderJobNotifyTheme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: "#9ca3af",
    primary25: "rgba(156,163,175,0.15)",
    primary50: "rgba(156,163,175,0.25)",
  },
});

const normalizeEmailKey = (e: string) => e.trim().toLowerCase();

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

type NotifyEmailHydrate = {
  selected: NotifyAgentOption[];
  manualChips: string[];
  baseline: NotificationEmailEntry[];
};

function hydrateNotifyEmailSlice(
  emails: string[],
  team: SignageDesignerSelectOption[]
): NotifyEmailHydrate {
  const byNorm = new Map<string, SignageDesignerSelectOption>();
  for (const o of team) {
    const em = String(o.email ?? "").trim();
    if (!em) continue;
    const k = normalizeEmailKey(em);
    if (!byNorm.has(k)) byNorm.set(k, o);
  }
  const selectedById = new Map<string, NotifyAgentOption>();
  const manual: string[] = [];
  const baselineByNorm = new Map<string, NotificationEmailEntry>();
  const manualSeenNorm = new Set<string>();
  for (const rawEm of emails) {
    const em = rawEm.trim();
    if (!em) continue;
    const hit = byNorm.get(normalizeEmailKey(em));
    if (hit?.value) {
      const id = String(hit.value);
      selectedById.set(id, {
        value: id,
        label: String(hit.label ?? ""),
        email: String(hit.email ?? "").trim() || undefined,
        licenseType: String(hit.licenseType || "").trim() || undefined,
      });
      baselineByNorm.set(normalizeEmailKey(em), {
        value: em,
        license_type: String(hit.licenseType || NO_LICENSE),
      });
    } else {
      const nk = normalizeEmailKey(em);
      if (!manualSeenNorm.has(nk)) {
        manualSeenNorm.add(nk);
        manual.push(em.trim());
      }
      baselineByNorm.set(nk, {
        value: em,
        license_type: NO_LICENSE,
      });
    }
  }
  return {
    selected: Array.from(selectedById.values()),
    manualChips: manual,
    baseline: Array.from(baselineByNorm.values()),
  };
}

function makeNotifyAsyncOption(
  selected: NotifyAgentOption[],
  toggle: (value: string, label: string, email?: string, licenseType?: string) => void
) {
  return function NotifyAsyncOption(props: any) {
    const { innerProps, data } = props;
    const { onMouseDown: _innerMd, ...innerRest } = innerProps || {};
    const isSelected = selected.some((o) => o.value === data.value);
    return (
      <components.Option {...props}>
        <div
          className="d-flex align-items-center gap-2"
          {...innerRest}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle(
              String(data.value),
              String(data.label ?? ""),
              String(data.email ?? "").trim() || undefined,
              String(data.licenseType ?? "").trim() || undefined
            );
          }}
          style={{ cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            tabIndex={-1}
            style={{
              accentColor: "#155dfc",
              transform: "scale(1.1)",
              pointerEvents: "none",
            }}
          />
          <span>{data.label}</span>
        </div>
      </components.Option>
    );
  };
}

function OrderJobNotifyChips({
  options,
  onRemove,
}: {
  options: NotifyAgentOption[];
  onRemove: (id: string) => void;
}) {
  if (!options.length) return null;
  return (
    <div className="d-flex flex-wrap gap-2 mt-2">
      {options.map((o) => (
        <span
          key={o.value}
          className="d-inline-flex align-items-center gap-1 px-3 py-2 br-8"
          style={{
            backgroundColor: "#dbeafe",
            color: "#1e40af",
            fontSize: "0.9rem",
            fontWeight: 500,
            borderRadius: "10px",
          }}
        >
          <span>{o.label}</span>
          <button
            type="button"
            className="btn btn-link p-0 m-0 text-decoration-none"
            style={{ color: "#2563eb", lineHeight: 1, minWidth: "1.25rem" }}
            aria-label={`Remove ${o.label}`}
            onClick={() => onRemove(o.value)}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

/** Sentinel value for the menu "Clear selection" row (not a real user id). */
const QUOTE_ASSIGNEE_CLEAR_VALUE = "__gs_quote_assign_clear__";

const clearQuoteAssigneeMenuOption: SignageDesignerSelectOption = {
  value: QUOTE_ASSIGNEE_CLEAR_VALUE,
  label: "Clear selection",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

const QuoteAssigneeDropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

const quoteAssigneeAsyncSelectStyles = {
  dropdownIndicator: (provided: Record<string, unknown>) => ({
    ...provided,
    display: "flex",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: Record<string, unknown>) => ({
    ...provided,
    display: "none",
  }),
};

const QuoteAssigneeOptionRow = (props: Parameters<typeof components.Option>[0] & { data: SignageDesignerSelectOption }) => {
  const isClear = props.data?.value === QUOTE_ASSIGNEE_CLEAR_VALUE;
  return (
    <components.Option {...props}>
      <span
        className={isClear ? "text-muted" : undefined}
        style={isClear ? { fontStyle: "italic" } : undefined}
      >
        {props.children}
      </span>
    </components.Option>
  );
};

/** Always show "Clear selection" at the top (independent of async page loading). */
const QuoteAssigneeMenuList = (props: any) => {
  const pickClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const onChange = props.selectProps?.onChange as
      | ((opt: SignageDesignerSelectOption | null, meta: unknown) => void)
      | undefined;
    onChange?.(clearQuoteAssigneeMenuOption, {
      action: "select-option",
      option: clearQuoteAssigneeMenuOption,
    });
  };
  return (
    <components.MenuList {...props}>
      <div
        className="gs-quote-assign-clear-row"
        role="option"
        aria-selected="false"
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          borderBottom: "1px solid var(--bs-border-color, #dee2e6)",
          fontStyle: "italic",
          color: "#6c757d",
          fontSize: "0.925rem",
        }}
        onMouseDown={pickClear}
      >
        Clear selection
      </div>
      {props.children}
    </components.MenuList>
  );
};

export const GENERAL_SETTINGS_KEYS = {
  CUSTOM_PRODUCT: "products.custom_product",
  AUTO_LINKING: "product.auto_linking",
  QUOTE_MODE: "order.quote_mode",
} as const;

export interface GeneralSettingItem {
  _id?: string;
  key: string;
  enabled: boolean;
  description?: string;
  updated_at?: string;
}

interface ListResponse {
  results: GeneralSettingItem[];
}

type ProjectSettingsGetResponse = {
  quote_assign_enabled?: boolean;
  quote_assignee_id?: string;
  quote_assignee_name?: string;
  quote_assignee_email?: string;
  payment_email?: string;
  mail_notify?: boolean;
  project_settings?: {
    quote_assign_enabled?: boolean;
    quote_assignee_id?: string;
    quote_assignee_name?: string;
    quote_assignee_email?: string;
    payment_email?: string;
    mail_notify?: boolean;
  };
};

const SignageGeneralSettings: React.FC = () => {
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);

  const [settings, setSettings] = useState<GeneralSettingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [customProductEnabled, setCustomProductEnabled] = useState(false);
  const [autoLinkingEnabled, setAutoLinkingEnabled] = useState(false);
  const [quoteModeEnabled, setQuoteModeEnabled] = useState(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [quoteAssigneeOption, setQuoteAssigneeOption] = useState<SignageDesignerSelectOption | null>(null);
  const [quoteAssignSaving, setQuoteAssignSaving] = useState(false);
  const [quoteAssigneeCleared, setQuoteAssigneeCleared] = useState(false);
  const [quoteAssignMailNotify, setQuoteAssignMailNotify] = useState(false);

  const [salesNotifySelected, setSalesNotifySelected] = useState<NotifyAgentOption[]>([]);
  const [salesNotifyManualChips, setSalesNotifyManualChips] = useState<string[]>([]);
  const [salesNotifyEmailDraft, setSalesNotifyEmailDraft] = useState("");
  const [salesNotifyBaseline, setSalesNotifyBaseline] = useState<NotificationEmailEntry[]>([]);
  const [salesNotifySaving, setSalesNotifySaving] = useState(false);

  const [installNotifySelected, setInstallNotifySelected] = useState<NotifyAgentOption[]>([]);
  const [installNotifyManualChips, setInstallNotifyManualChips] = useState<string[]>([]);
  const [installNotifyEmailDraft, setInstallNotifyEmailDraft] = useState("");
  const [installNotifyBaseline, setInstallNotifyBaseline] = useState<NotificationEmailEntry[]>([]);
  const [installNotifySaving, setInstallNotifySaving] = useState(false);

  const [removalNotifySelected, setRemovalNotifySelected] = useState<NotifyAgentOption[]>([]);
  const [removalNotifyManualChips, setRemovalNotifyManualChips] = useState<string[]>([]);
  const [removalNotifyEmailDraft, setRemovalNotifyEmailDraft] = useState("");
  const [removalNotifyBaseline, setRemovalNotifyBaseline] = useState<NotificationEmailEntry[]>([]);
  const [removalNotifySaving, setRemovalNotifySaving] = useState(false);

  const [shippingHandlingCharges, setShippingHandlingCharges] = useState("");
  const [shippingHandlingLoading, setShippingHandlingLoading] = useState(false);
  const [shippingHandlingSaving, setShippingHandlingSaving] = useState(false);

  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const signageFirmID = isSignageTeammember ? parentSignageId || "" : userId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const isTeammember = location.pathname.includes("/signageteammember");
      if (isTeammember && userId) {
        try {
          const hierarchy = await fetchParentHierarchy(token);
          if (!cancelled && hierarchy?.parentSignageId) setParentSignageId(hierarchy.parentSignageId);
        } catch {
          if (!cancelled) setParentSignageId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, userId, token]);

  const fetchSettings = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(SETTINGS_URL, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load settings");
      const data: ListResponse = await res.json();
      const list = Array.isArray(data?.results) ? data.results : [];
      setSettings(list);
      const custom = list.find((s) => s.key === GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT);
      setCustomProductEnabled(custom?.enabled ?? false);
      const autoLink = list.find((s) => s.key === GENERAL_SETTINGS_KEYS.AUTO_LINKING);
      setAutoLinkingEnabled(autoLink?.enabled ?? false);
      const quoteMode = list.find((s) => s.key === GENERAL_SETTINGS_KEYS.QUOTE_MODE);
      setQuoteModeEnabled(quoteMode?.enabled ?? false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load settings");
      setSettings([]);
      setCustomProductEnabled(false);
      setAutoLinkingEnabled(false);
      setQuoteModeEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(PROJECT_SETTINGS_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const raw: ProjectSettingsGetResponse = await res.json().catch(() => ({}));
        if (!res.ok) return;
        // Merge nested project_settings with root so top-level fields (e.g. mail_notify) are never dropped.
        const nested =
          raw?.project_settings && typeof raw.project_settings === "object"
            ? raw.project_settings
            : null;
        const ps = nested ? { ...nested, ...raw } : raw;
        const assigneeId = String(ps?.quote_assignee_id || "").trim();
        const assigneeName = String(ps?.quote_assignee_name || "").trim();
        const assigneeEmail = String(ps?.payment_email ?? ps?.quote_assignee_email ?? "").trim();
        if (cancelled) return;
        setQuoteAssignMailNotify(ps.mail_notify === true);

        if (assigneeId) {
          setQuoteAssigneeOption({
            value: assigneeId,
            label: assigneeName || assigneeId,
            firstName: "",
            lastName: "",
            email: assigneeEmail,
            phone: "",
            licenseType: String(quoteAssigneeOption?.licenseType || "SIGNAGE_TEAM_MEMBER"),
          });
          setQuoteAssigneeCleared(false);
        } else {
          setQuoteAssigneeOption(null);
          setQuoteAssigneeCleared(Boolean(ps && ps.quote_assign_enabled === false));
        }
      } catch {
        if (!cancelled) {
          setQuoteAssigneeOption(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, signageFirmID]);

  const buildQuoteAssignPostBody = useCallback(async (): Promise<ProjectSettingsQuoteAssignPayload> => {
    let paymentEmailResolved = "";
    if (!quoteAssigneeCleared && quoteAssigneeOption?.value) {
      paymentEmailResolved = String(quoteAssigneeOption.email ?? "").trim();
      if (!paymentEmailResolved) {
        const userRow = await fetchUserData(quoteAssigneeOption.value, token);
        paymentEmailResolved = String(userRow?.email ?? "").trim();
      }
    }
    if (quoteAssigneeCleared) {
      return {
        quote_assign_enabled: false,
        quote_assignee_id: "",
        quote_assignee_name: "",
        payment_email: "",
        mail_notify: quoteAssignMailNotify,
        licenseType: "",
      };
    }
    return {
      quote_assign_enabled: quoteModeEnabled,
      quote_assignee_id: quoteAssigneeOption?.value ?? "",
      quote_assignee_name: quoteAssigneeOption?.label ?? "",
      payment_email: paymentEmailResolved,
      mail_notify: quoteAssignMailNotify,
      licenseType: String(quoteAssigneeOption?.licenseType || "SIGNAGE_TEAM_MEMBER"),
    };
  }, [
    token,
    quoteAssigneeCleared,
    quoteModeEnabled,
    quoteAssigneeOption,
    quoteAssignMailNotify,
  ]);

  const handleSaveQuoteAssignSettings = useCallback(async () => {
    if (!token) return;
    setQuoteAssignSaving(true);
    try {
      const payload = await buildQuoteAssignPostBody();
      await postProjectSettingsQuoteAssign(token, payload);
      toast.success("Quote assignment settings saved successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save quote assignee settings");
    } finally {
      setQuoteAssignSaving(false);
    }
  }, [token, buildQuoteAssignPostBody]);

  const loadOrderJobNotifyAgentOptions: LoadOptions<NotifyAgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (!token || !signageFirmID) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      try {
        const { options, hasMore } = await fetchUsersSearchSignageTeamMembers(token, signageFirmID, {
          page,
          limit: QUOTE_ASSIGNEE_SEARCH_PAGE_SIZE,
          search: (inputValue || "").trim(),
        });
        const mappedOptions: NotifyAgentOption[] = options.map((o) => ({
          value: String(o.value ?? ""),
          label: String(o.label ?? ""),
          email: String(o.email ?? "").trim() || undefined,
          licenseType: String(o.licenseType || "").trim() || undefined,
        }));
        return {
          options: mappedOptions,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load agents");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, signageFirmID]
  );

  const loadAllSignageTeamMembersForMatch = useCallback(async (firmId: string) => {
    const merged: SignageDesignerSelectOption[] = [];
    let page = 1;
    const limit = 50;
    for (;;) {
      const { options, hasMore } = await fetchUsersSearchSignageTeamMembers(token, firmId, {
        page,
        limit,
        search: "",
      });
      merged.push(...options);
      if (!hasMore || options.length === 0) break;
      page += 1;
      if (page > 80) break;
    }
    return merged;
  }, [token]);

  useEffect(() => {
    if (!token || !signageFirmID) return;
    let cancelled = false;
    (async () => {
      try {
        const team = await loadAllSignageTeamMembersForMatch(signageFirmID);
        if (cancelled) return;
        const [salesRes, installRes, removalRes] = await Promise.all([
          fetchNotificationEmailsSettingByKey(token, SALES_ORDER_NOTIFICATION_EMAILS_KEY),
          fetchNotificationEmailsSettingByKey(token, INSTALLATION_ORDER_NOTIFICATION_EMAILS_KEY),
          fetchNotificationEmailsSettingByKey(token, REMOVAL_ORDER_NOTIFICATION_EMAILS_KEY),
        ]);
        if (cancelled) return;
        const sales = hydrateNotifyEmailSlice(salesRes.emails, team);
        setSalesNotifySelected(sales.selected);
        setSalesNotifyManualChips(sales.manualChips);
        setSalesNotifyEmailDraft("");
        setSalesNotifyBaseline(sales.baseline);

        const inst = hydrateNotifyEmailSlice(installRes.emails, team);
        setInstallNotifySelected(inst.selected);
        setInstallNotifyManualChips(inst.manualChips);
        setInstallNotifyEmailDraft("");
        setInstallNotifyBaseline(inst.baseline);

        const rem = hydrateNotifyEmailSlice(removalRes.emails, team);
        setRemovalNotifySelected(rem.selected);
        setRemovalNotifyManualChips(rem.manualChips);
        setRemovalNotifyEmailDraft("");
        setRemovalNotifyBaseline(rem.baseline);
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : "Failed to load notification emails");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, signageFirmID, loadAllSignageTeamMembersForMatch]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setShippingHandlingLoading(true);
      try {
        const res = await fetch(
          `${SETTINGS_URL}/bykey?key=${encodeURIComponent(SHIPPING_HANDLING_CHARGES_KEY)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (res.status === 404) {
          if (!cancelled) setShippingHandlingCharges("");
          return;
        }
        if (!res.ok) throw new Error("Failed to load shipping handling charges");
        const raw = await res.json().catch(() => ({}));
        const setting = (raw as any)?.data?.setting ?? (raw as any)?.setting;
        const amount = setting?.shipping_handling_charges;
        if (!cancelled) {
          setShippingHandlingCharges(
            amount === null || amount === undefined || amount === ""
              ? ""
              : String(amount)
          );
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : "Failed to load shipping handling charges");
          setShippingHandlingCharges("");
        }
      } finally {
        if (!cancelled) setShippingHandlingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSaveShippingHandlingCharges = useCallback(async () => {
    if (!token) return;
    const trimmed = shippingHandlingCharges.trim();
    if (!trimmed) {
      toast.error("Enter a shipping handling charge amount");
      return;
    }
    const amount = Number(trimmed);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid amount (0 or greater)");
      return;
    }
    setShippingHandlingSaving(true);
    try {
      const res = await fetch(SETTINGS_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          key: SHIPPING_HANDLING_CHARGES_KEY,
          shipping_handling_charges: amount,
          description: SHIPPING_HANDLING_CHARGES_DESCRIPTION,
        }),
      });
      const raw = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          (raw as any)?.error || (raw as any)?.message || "Failed to save shipping handling charges";
        throw new Error(typeof message === "string" ? message : "Failed to save shipping handling charges");
      }
      const setting = (raw as any)?.data?.setting ?? (raw as any)?.setting;
      const saved = setting?.shipping_handling_charges;
      if (saved !== null && saved !== undefined) {
        setShippingHandlingCharges(String(saved));
      }
      toast.success("Shipping handling charges saved successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save shipping handling charges");
    } finally {
      setShippingHandlingSaving(false);
    }
  }, [token, shippingHandlingCharges]);

  const collectNotifyEmails = useCallback(
    async (
      selected: NotifyAgentOption[],
      manualChips: string[]
    ): Promise<NotificationEmailEntry[]> => {
      const normToEntry = new Map<string, NotificationEmailEntry>();
      const push = (display: string, licenseType?: string) => {
        const t = String(display ?? "").trim();
        if (!t || !isValidEmail(t)) return;
        const k = normalizeEmailKey(t);
        if (!normToEntry.has(k)) {
          normToEntry.set(k, {
            value: t,
            license_type: String(licenseType || NO_LICENSE).trim() || NO_LICENSE,
          });
        }
      };
      for (const s of selected) {
        let email = String(s.email ?? "").trim();
        if (!email && s.value) {
          const userRow = await fetchUserData(s.value, token);
          email = String(userRow?.email ?? "").trim();
        }
        if (email) push(email, s.licenseType);
      }
      for (const m of manualChips) {
        if (isValidEmail(m)) push(m, NO_LICENSE);
      }
      return Array.from(normToEntry.values());
    },
    [token]
  );

  type NotifyKind = "sales" | "installation" | "removal";

  const NOTIFY_KEYS: Record<NotifyKind, string> = {
    sales: SALES_ORDER_NOTIFICATION_EMAILS_KEY,
    installation: INSTALLATION_ORDER_NOTIFICATION_EMAILS_KEY,
    removal: REMOVAL_ORDER_NOTIFICATION_EMAILS_KEY,
  };

  const handleSaveNotifySettings = useCallback(
    async (kind: NotifyKind) => {
      if (!token) return;
      const key = NOTIFY_KEYS[kind];
      const draft =
        kind === "sales"
          ? salesNotifyEmailDraft.trim()
          : kind === "installation"
            ? installNotifyEmailDraft.trim()
            : removalNotifyEmailDraft.trim();
      const selected =
        kind === "sales"
          ? salesNotifySelected
          : kind === "installation"
            ? installNotifySelected
            : removalNotifySelected;
      const manualChips =
        kind === "sales"
          ? salesNotifyManualChips
          : kind === "installation"
            ? installNotifyManualChips
            : removalNotifyManualChips;
      const baseline =
        kind === "sales"
          ? salesNotifyBaseline
          : kind === "installation"
            ? installNotifyBaseline
            : removalNotifyBaseline;
      const setBaseline =
        kind === "sales"
          ? setSalesNotifyBaseline
          : kind === "installation"
            ? setInstallNotifyBaseline
            : setRemovalNotifyBaseline;
      const setManualChips =
        kind === "sales"
          ? setSalesNotifyManualChips
          : kind === "installation"
            ? setInstallNotifyManualChips
            : setRemovalNotifyManualChips;
      const setDraft =
        kind === "sales"
          ? setSalesNotifyEmailDraft
          : kind === "installation"
            ? setInstallNotifyEmailDraft
            : setRemovalNotifyEmailDraft;
      const setSaving =
        kind === "sales"
          ? setSalesNotifySaving
          : kind === "installation"
            ? setInstallNotifySaving
            : setRemovalNotifySaving;

      if (draft && !isValidEmail(draft)) {
        toast.error("Enter a valid email in Notify these emails, or clear it.");
        return;
      }
      setSaving(true);
      try {
        let current = await collectNotifyEmails(selected, manualChips);
        if (draft && isValidEmail(draft)) {
          const k = normalizeEmailKey(draft);
          const merged = new Map(current.map((e) => [normalizeEmailKey(e.value), e]));
          merged.set(k, { value: draft.trim(), license_type: NO_LICENSE });
          current = Array.from(merged.values());
        }
        const add = current.filter(
          (c) => !baseline.some((b) => normalizeEmailKey(b.value) === normalizeEmailKey(c.value))
        );
        const remove = baseline.filter(
          (b) => !current.some((c) => normalizeEmailKey(b.value) === normalizeEmailKey(c.value))
        );
        if (add.length === 0 && remove.length === 0) {
          toast.info("No email changes to save.");
          return;
        }
        await patchGeneralNotificationEmails(token, {
          key,
          emails: { add, remove },
          enabled: current.length > 0,
        });
        if (draft && isValidEmail(draft)) {
          setManualChips((prev) =>
            prev.some((e) => normalizeEmailKey(e) === normalizeEmailKey(draft)) ? prev : [...prev, draft.trim()]
          );
          setDraft("");
        }
        setBaseline(current);
        toast.success("Notification emails updated successfully");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to save notification settings");
      } finally {
        setSaving(false);
      }
    },
    [
      token,
      collectNotifyEmails,
      salesNotifyEmailDraft,
      installNotifyEmailDraft,
      removalNotifyEmailDraft,
      salesNotifySelected,
      installNotifySelected,
      removalNotifySelected,
      salesNotifyManualChips,
      installNotifyManualChips,
      removalNotifyManualChips,
      salesNotifyBaseline,
      installNotifyBaseline,
      removalNotifyBaseline,
    ]
  );

  const handleAddManualNotifyEmail = useCallback((kind: NotifyKind) => {
    const draft =
      kind === "sales"
        ? salesNotifyEmailDraft.trim()
        : kind === "installation"
          ? installNotifyEmailDraft.trim()
          : removalNotifyEmailDraft.trim();
    if (!draft) return;
    if (!isValidEmail(draft)) {
      toast.error("Invalid email");
      return;
    }
    const setManual =
      kind === "sales"
        ? setSalesNotifyManualChips
        : kind === "installation"
          ? setInstallNotifyManualChips
          : setRemovalNotifyManualChips;
    const setDraft =
      kind === "sales"
        ? setSalesNotifyEmailDraft
        : kind === "installation"
          ? setInstallNotifyEmailDraft
          : setRemovalNotifyEmailDraft;
    setManual((prev) =>
      prev.some((x) => normalizeEmailKey(x) === normalizeEmailKey(draft)) ? prev : [...prev, draft]
    );
    setDraft("");
  }, [salesNotifyEmailDraft, installNotifyEmailDraft, removalNotifyEmailDraft]);

  const toggleSalesNotify = useCallback((value: string, label: string, email?: string, licenseType?: string) => {
    setSalesNotifySelected((prev) => {
      const exists = prev.some((o) => o.value === value);
      if (exists) return prev.filter((o) => o.value !== value);
      return [...prev, { value, label, email, licenseType }];
    });
  }, []);

  const toggleInstallNotify = useCallback((value: string, label: string, email?: string, licenseType?: string) => {
    setInstallNotifySelected((prev) => {
      const exists = prev.some((o) => o.value === value);
      if (exists) return prev.filter((o) => o.value !== value);
      return [...prev, { value, label, email, licenseType }];
    });
  }, []);

  const toggleRemovalNotify = useCallback((value: string, label: string, email?: string, licenseType?: string) => {
    setRemovalNotifySelected((prev) => {
      const exists = prev.some((o) => o.value === value);
      if (exists) return prev.filter((o) => o.value !== value);
      return [...prev, { value, label, email, licenseType }];
    });
  }, []);

  const salesNotifyOptionComponent = useMemo(
    () => makeNotifyAsyncOption(salesNotifySelected, toggleSalesNotify),
    [salesNotifySelected, toggleSalesNotify]
  );
  const installNotifyOptionComponent = useMemo(
    () => makeNotifyAsyncOption(installNotifySelected, toggleInstallNotify),
    [installNotifySelected, toggleInstallNotify]
  );
  const removalNotifyOptionComponent = useMemo(
    () => makeNotifyAsyncOption(removalNotifySelected, toggleRemovalNotify),
    [removalNotifySelected, toggleRemovalNotify]
  );

  const loadQuoteAssigneeOptions: LoadOptions<
    SignageDesignerSelectOption,
    any,
    { page: number }
  > = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      if (!token || !signageFirmID) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      try {
        const { options, hasMore } = await fetchUsersSearchSignageTeamMembers(token, signageFirmID, {
          page,
          limit: QUOTE_ASSIGNEE_SEARCH_PAGE_SIZE,
          search: q,
        });
        const withoutSentinel = options.filter((o) => o.value !== QUOTE_ASSIGNEE_CLEAR_VALUE);
        return {
          options: withoutSentinel,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load team members");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, signageFirmID]
  );

const upsertSetting = async (
  key: string,
  enabled: boolean,
  description?: string
) => {
  if (!token) return;

  setTogglingKey(key);

  try {
    const payload = {
      key,
      enabled,
      ...(description ? { description } : {}),
    };

    const res = await fetch(SETTINGS_URL, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Failed to update setting");

    // Update correct toggle state
    if (key === GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT) {
      setCustomProductEnabled(enabled);
    }

    if (key === GENERAL_SETTINGS_KEYS.AUTO_LINKING) {
      setAutoLinkingEnabled(enabled);
    }

    if (key === GENERAL_SETTINGS_KEYS.QUOTE_MODE) {
      setQuoteModeEnabled(enabled);
    }

    // Update local settings list
    setSettings((prev) => {
      const rest = prev.filter((s) => s.key !== key);
      return [
        ...rest,
        {
          key,
          enabled,
          description,
        },
      ];
    });

    toast.success(enabled ? "Setting enabled successfully" : "Setting disabled successfully");
  } catch (e) {
    toast.error(e instanceof Error ? e.message : "Failed to update setting");
  } finally {
    setTogglingKey(null);
  }
};

  const handleCustomProductToggle = (enabled: boolean) => {
    upsertSetting(
      GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT,
      enabled,
      "Enable this feature to allow custom product creation."
    );
  };

  const handleAutoLinkingToggle = (enabled: boolean) => {
    upsertSetting(
      GENERAL_SETTINGS_KEYS.AUTO_LINKING,
      enabled,
      "Enable this feature to allow products auto linking."
    );
  };

  const handleQuoteModeToggle = (enabled: boolean) => {
    upsertSetting(
      GENERAL_SETTINGS_KEYS.QUOTE_MODE,
      enabled,
      "Enable this: your sales and installation draft order status will show quote mode."
    );
  };

  const isSettingToggleBusy = (key: string) => togglingKey === key;

return (
  <div className="container py-5">
    <div className="row justify-content-center">
      <div className="col-12 col-lg-9">

        {/* Main Card */}
        <div
          className="bg-white shadow-sm p-4"
          style={{
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
          }}
        >

          {/* Header */}
          <div className="d-flex align-items-start gap-3">

            {/* Icon Box */}
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "44px",
                height: "44px",
                backgroundColor: "#E8F0FE",
                borderRadius: "10px",
              }}
            >
              <SettingsIcon size={20} color="#2563EB" />
            </div>

            {/* Title + Description */}
            <div>
              <h5 className="mb-1 fw-semibold text-dark">
                General Settings
              </h5>
              <p
                className="mb-0"
                style={{ fontSize: "14px", color: "#6B7280" }}
              >
                Configure general application settings and features.
              </p>
            </div>
          </div>

          {/* Product Settings Section */}
          <div className="mt-4">

            <div className="d-flex align-items-center gap-2 mb-2">
              <Package size={18} color="#6B7280" />
              <span className="fw-semibold text-dark">
                Product Settings
              </span>
            </div>

            {/* Divider Line */}
            <hr style={{ borderColor: "#E5E7EB" }} />

            {loading ? (
              <div className="py-3 text-muted">Loading...</div>
            ) : (
              <>
              <div
                className="d-flex justify-content-between align-items-center p-3"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                {/* Left Content */}
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium text-dark">
                      Custom Product
                    </span>
                    <Info size={16} color="#9CA3AF" />
                  </div>

                  {/* Increased Font Size */}
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    Enable this feature to allow custom product creation.
                  </p>
                </div>

                {/* Toggle */}
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="custom-product-toggle"
                    checked={customProductEnabled}
                    disabled={isSettingToggleBusy(GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCustomProductToggle(e.target.checked);
                    }}
                    style={{
                      width: "44px",
                      height: "22px",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center p-3 mt-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                {/* Left Content */}
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium text-dark">
                      Products Auto Linking
                    </span>
                    <Info size={16} color="#9CA3AF" />
                  </div>

                  {/* Increased Font Size */}
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    Enable this feature to allow  Products Auto Linking creation.
                  </p>
                </div>

                {/* Toggle */}
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="auto-linking-toggle"
                    disabled={isSettingToggleBusy(GENERAL_SETTINGS_KEYS.AUTO_LINKING)}
                    checked={autoLinkingEnabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAutoLinkingToggle(e.target.checked);
                    }}
                    style={{
                      width: "44px",
                      height: "22px",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </div>

              <div
                className="p-3 mt-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-medium text-dark">Quote Mode</span>
                      <Info size={16} color="#9CA3AF" />
                    </div>
                    <p
                      className="mb-0 mt-1"
                      style={{
                        fontSize: "15px",
                        color: "#6B7280",
                      }}
                    >
                      Enable this: your sales and installation draft order status will show quote
                      mode.
                    </p>

                    <div className="mt-3">
                      <label
                        className="form-label mb-1 fw-medium text-dark"
                        htmlFor="quote-assignee-select-input"
                        style={{ fontSize: "14px" }}
                      >
                        Auto-Assign Responsible Member After Design Approval
                      </label>
                      <div className="mt-1" style={{ maxWidth: "420px" }}>
                        <AsyncPaginate
                          key={signageFirmID || "firm"}
                          inputId="quote-assignee-select-input"
                          classNamePrefix="gs-quote-assign-rs"
                          placeholder="Auto-Assign Responsible Member After Design Approval"
                          isDisabled={quoteAssignSaving || !signageFirmID}
                          value={
                            quoteAssigneeOption
                              ? {
                                  value: quoteAssigneeOption.value,
                                  label: quoteAssigneeOption.label,
                                  firstName: quoteAssigneeOption.firstName,
                                  lastName: quoteAssigneeOption.lastName,
                                  email: quoteAssigneeOption.email,
                                  phone: quoteAssigneeOption.phone,
                                }
                              : null
                          }
                          loadOptions={loadQuoteAssigneeOptions}
                          onChange={(opt) => {
                            const raw = opt as SignageDesignerSelectOption | null;
                            if (raw?.value === QUOTE_ASSIGNEE_CLEAR_VALUE) {
                              setQuoteAssigneeOption(null);
                              setQuoteAssigneeCleared(true);
                              return;
                            }
                            const next = raw;
                            setQuoteAssigneeOption(next);
                            setQuoteAssigneeCleared(false);
                            if (next?.value && !String(next.email ?? "").trim() && token) {
                              void fetchUserData(next.value, token).then((u) => {
                                const em = String(u?.email ?? "").trim();
                                if (!em) return;
                                setQuoteAssigneeOption((prev) =>
                                  prev?.value === next.value ? { ...prev, email: em } : prev
                                );
                              });
                            }
                          }}
                          additional={{ page: 1 }}
                          defaultOptions
                          debounceTimeout={300}
                          isClearable
                          components={{
                            DropdownIndicator: QuoteAssigneeDropdownIndicator,
                            MenuList: QuoteAssigneeMenuList as any,
                            Option: QuoteAssigneeOptionRow as any,
                          }}
                          styles={quoteAssigneeAsyncSelectStyles as any}
                        />
                      </div>
                      {!quoteModeEnabled && (
                        <div
                          className="mt-2 p-2 rounded-2"
                          style={{
                            maxWidth: "420px",
                            backgroundColor: "#F3F4F6",
                            border: "1px solid #E5E7EB",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                        >
                          Quote Mode is off: draft orders will not use quote mode until you turn
                          it on. You can still choose a responsible member here; that choice is
                          saved and used when Quote Mode is on.
                        </div>
                      )}
                      <div className="form-check mt-3 mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="quote-assign-mail-notify"
                          checked={quoteAssignMailNotify}
                          disabled={quoteAssignSaving}
                          onChange={(e) => setQuoteAssignMailNotify(e.target.checked)}
                        />
                        <label
                          className="form-check-label text-dark"
                          htmlFor="quote-assign-mail-notify"
                          style={{ fontSize: "14px", cursor: "pointer" }}
                        >
                          Send email
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-check form-switch m-0 flex-shrink-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="quote-mode-toggle"
                      disabled={isSettingToggleBusy(GENERAL_SETTINGS_KEYS.QUOTE_MODE)}
                      checked={quoteModeEnabled}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleQuoteModeToggle(e.target.checked);
                      }}
                      style={{
                        width: "44px",
                        height: "22px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary  table-addbutton"
                    disabled={quoteAssignSaving}
                    onClick={() => {
                      void handleSaveQuoteAssignSettings();
                    }}
                  >
                    {quoteAssignSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>

              <div
                className="p-3 mt-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium text-dark">Order, Job & Payment Notifications</span>
                    <Info size={16} color="#9CA3AF" />
                  </div>
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    Receive alerts when new orders are submitted by brokerage firms or agents, and get notified when payments are completed for quotes, orders.
                  </p>

                  <div className="mt-3">
                    <label
                      className="form-label mb-1 fw-medium text-dark d-flex align-items-center gap-2"
                      htmlFor="sales-notify-agents-input"
                      style={{ fontSize: "14px" }}
                    >
                      <Users size={18} style={{ color: "#155dfc" }} />
                      <span>Notify these Team Members</span>
                    </label>
                    <div className="mt-1" style={{ maxWidth: "520px" }}>
                      <AsyncPaginate
                        inputId="sales-notify-agents-input"
                        key={`sales-oj-${signageFirmID || "firm"}`}
                        classNamePrefix="gs-sales-notify-rs"
                        placeholder="Search and select Team Members..."
                        isDisabled={salesNotifySaving || !signageFirmID}
                        value={[]}
                        loadOptions={loadOrderJobNotifyAgentOptions}
                        onChange={() => undefined}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        isMulti
                        theme={orderJobNotifyTheme}
                        components={{
                          DropdownIndicator: OrderJobNotifyDropdownIndicator,
                          Option: salesNotifyOptionComponent as any,
                        }}
                        styles={orderJobNotifyMultiStyles(false) as any}
                      />
                    </div>
                    <OrderJobNotifyChips
                      options={salesNotifySelected}
                      onRemove={(id) =>
                        setSalesNotifySelected((prev) => prev.filter((o) => o.value !== id))
                      }
                    />
                    <div className="mt-3" style={{ maxWidth: "520px" }}>
                      <label
                        className="form-label mb-1 fw-medium text-dark"
                        htmlFor="sales-notify-email-input"
                        style={{ fontSize: "14px" }}
                      >
                        Notify these emails
                      </label>
                      <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
                        <input
                          id="sales-notify-email-input"
                          type="email"
                          className="form-control"
                          placeholder="Enter the email here"
                          value={salesNotifyEmailDraft}
                          disabled={salesNotifySaving}
                          onChange={(e) => setSalesNotifyEmailDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            handleAddManualNotifyEmail("sales");
                          }}
                          style={{
                            minHeight: "39px",
                            borderRadius: "10px",
                            backgroundColor: "#f5f6fa",
                            border: "2px solid #c9ced6",
                          }}
                        />
                        <button
                          type="button"
                          className="btn"
                          disabled={salesNotifySaving || !salesNotifyEmailDraft.trim()}
                          onClick={() => handleAddManualNotifyEmail("sales")}
                          style={{
                            minHeight: "39px",
                            borderRadius: "12px",
                            border: "1px solid #9CA3AF",
                            backgroundColor: "#F8FAFC",
                            color: "#6B7280",
                            fontWeight: 600,
                            padding: "0 14px",
                            paddingTop: "3px",
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          + Add Email
                        </button>
                      </div>
                      {salesNotifyManualChips.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {salesNotifyManualChips.map((em) => (
                            <span
                              key={normalizeEmailKey(em)}
                              className="d-inline-flex align-items-center gap-1 px-3 py-2 br-8"
                              style={{
                                backgroundColor: "#dbeafe",
                                color: "#1e40af",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                borderRadius: "10px",
                              }}
                            >
                              <span>{em}</span>
                              <button
                                type="button"
                                className="btn btn-link p-0 m-0 text-decoration-none"
                                style={{ color: "#2563eb", lineHeight: 1, minWidth: "1.25rem" }}
                                aria-label={`Remove ${em}`}
                                onClick={() =>
                                  setSalesNotifyManualChips((prev) =>
                                    prev.filter((x) => normalizeEmailKey(x) !== normalizeEmailKey(em))
                                  )
                                }
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton"
                    disabled={salesNotifySaving}
                    onClick={() => {
                      void handleSaveNotifySettings("sales");
                    }}
                  >
                    {salesNotifySaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              <div
                className="p-3 mt-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium text-dark">Installation Order Notifications</span>
                    <Info size={16} color="#9CA3AF" />
                  </div>
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    Receive notifications when new installation orders are created or submitted and when payments are completed.
                  </p>

                  <div className="mt-3">
                    <label
                      className="form-label mb-1 fw-medium text-dark d-flex align-items-center gap-2"
                      htmlFor="install-notify-agents-input"
                      style={{ fontSize: "14px" }}
                    >
                      <Users size={18} style={{ color: "#155dfc" }} />
                      <span>Notify these Team Members</span>
                    </label>
                    <div className="mt-1" style={{ maxWidth: "520px" }}>
                      <AsyncPaginate
                        inputId="install-notify-agents-input"
                        key={`install-oj-${signageFirmID || "firm"}`}
                        classNamePrefix="gs-install-notify-rs"
                        placeholder="Search and select Team Members..."
                        isDisabled={installNotifySaving || !signageFirmID}
                        value={[]}
                        loadOptions={loadOrderJobNotifyAgentOptions}
                        onChange={() => undefined}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        isMulti
                        theme={orderJobNotifyTheme}
                        components={{
                          DropdownIndicator: OrderJobNotifyDropdownIndicator,
                          Option: installNotifyOptionComponent as any,
                        }}
                        styles={orderJobNotifyMultiStyles(false) as any}
                      />
                    </div>
                    <OrderJobNotifyChips
                      options={installNotifySelected}
                      onRemove={(id) =>
                        setInstallNotifySelected((prev) => prev.filter((o) => o.value !== id))
                      }
                    />
                    <div className="mt-3" style={{ maxWidth: "520px" }}>
                      <label
                        className="form-label mb-1 fw-medium text-dark"
                        htmlFor="install-notify-email-input"
                        style={{ fontSize: "14px" }}
                      >
                        Notify these emails
                      </label>
                      <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
                        <input
                          id="install-notify-email-input"
                          type="email"
                          className="form-control"
                          placeholder="Enter the email here"
                          value={installNotifyEmailDraft}
                          disabled={installNotifySaving}
                          onChange={(e) => setInstallNotifyEmailDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            handleAddManualNotifyEmail("installation");
                          }}
                          style={{
                            minHeight: "39px",
                            borderRadius: "10px",
                            backgroundColor: "#f5f6fa",
                            border: "2px solid #c9ced6",
                          }}
                        />
                        <button
                          type="button"
                          className="btn"
                          disabled={installNotifySaving || !installNotifyEmailDraft.trim()}
                          onClick={() => handleAddManualNotifyEmail("installation")}
                          style={{
                            minHeight: "39px",
                            borderRadius: "12px",
                            border: "1px solid #9CA3AF",
                            backgroundColor: "#F8FAFC",
                            color: "#6B7280",
                            fontWeight: 600,
                            padding: "0 14px",
                            paddingTop: "3px",
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          + Add Email
                        </button>
                      </div>
                      {installNotifyManualChips.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {installNotifyManualChips.map((em) => (
                            <span
                              key={normalizeEmailKey(em)}
                              className="d-inline-flex align-items-center gap-1 px-3 py-2 br-8"
                              style={{
                                backgroundColor: "#dbeafe",
                                color: "#1e40af",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                borderRadius: "10px",
                              }}
                            >
                              <span>{em}</span>
                              <button
                                type="button"
                                className="btn btn-link p-0 m-0 text-decoration-none"
                                style={{ color: "#2563eb", lineHeight: 1, minWidth: "1.25rem" }}
                                aria-label={`Remove ${em}`}
                                onClick={() =>
                                  setInstallNotifyManualChips((prev) =>
                                    prev.filter((x) => normalizeEmailKey(x) !== normalizeEmailKey(em))
                                  )
                                }
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton"
                    disabled={installNotifySaving}
                    onClick={() => {
                      void handleSaveNotifySettings("installation");
                    }}
                  >
                    {installNotifySaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              <div
                className="p-3 mt-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium text-dark">Removal Order Notifications</span>
                    <Info size={16} color="#9CA3AF" />
                  </div>
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    Receive notifications when new removal orders are created or submitted and when payments are completed.
                  </p>

                  <div className="mt-3">
                    <label
                      className="form-label mb-1 fw-medium text-dark d-flex align-items-center gap-2"
                      htmlFor="removal-notify-agents-input"
                      style={{ fontSize: "14px" }}
                    >
                      <Users size={18} style={{ color: "#155dfc" }} />
                      <span>Notify these Team Members</span>
                    </label>
                    <div className="mt-1" style={{ maxWidth: "520px" }}>
                      <AsyncPaginate
                        inputId="removal-notify-agents-input"
                        key={`removal-oj-${signageFirmID || "firm"}`}
                        classNamePrefix="gs-removal-notify-rs"
                        placeholder="Search and select Team Members..."
                        isDisabled={removalNotifySaving || !signageFirmID}
                        value={[]}
                        loadOptions={loadOrderJobNotifyAgentOptions}
                        onChange={() => undefined}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        isMulti
                        theme={orderJobNotifyTheme}
                        components={{
                          DropdownIndicator: OrderJobNotifyDropdownIndicator,
                          Option: removalNotifyOptionComponent as any,
                        }}
                        styles={orderJobNotifyMultiStyles(false) as any}
                      />
                    </div>
                    <OrderJobNotifyChips
                      options={removalNotifySelected}
                      onRemove={(id) =>
                        setRemovalNotifySelected((prev) => prev.filter((o) => o.value !== id))
                      }
                    />
                    <div className="mt-3" style={{ maxWidth: "520px" }}>
                      <label
                        className="form-label mb-1 fw-medium text-dark"
                        htmlFor="removal-notify-email-input"
                        style={{ fontSize: "14px" }}
                      >
                        Notify these emails
                      </label>
                      <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
                        <input
                          id="removal-notify-email-input"
                          type="email"
                          className="form-control"
                          placeholder="Enter the email here"
                          value={removalNotifyEmailDraft}
                          disabled={removalNotifySaving}
                          onChange={(e) => setRemovalNotifyEmailDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            handleAddManualNotifyEmail("removal");
                          }}
                          style={{
                            minHeight: "39px",
                            borderRadius: "10px",
                            backgroundColor: "#f5f6fa",
                            border: "2px solid #c9ced6",
                          }}
                        />
                        <button
                          type="button"
                          className="btn"
                          disabled={removalNotifySaving || !removalNotifyEmailDraft.trim()}
                          onClick={() => handleAddManualNotifyEmail("removal")}
                          style={{
                            minHeight: "39px",
                            borderRadius: "12px",
                            border: "1px solid #9CA3AF",
                            backgroundColor: "#F8FAFC",
                            color: "#6B7280",
                            fontWeight: 600,
                            padding: "0 14px",
                            paddingTop: "3px",
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          + Add Email
                        </button>
                      </div>
                      {removalNotifyManualChips.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {removalNotifyManualChips.map((em) => (
                            <span
                              key={normalizeEmailKey(em)}
                              className="d-inline-flex align-items-center gap-1 px-3 py-2 br-8"
                              style={{
                                backgroundColor: "#dbeafe",
                                color: "#1e40af",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                borderRadius: "10px",
                              }}
                            >
                              <span>{em}</span>
                              <button
                                type="button"
                                className="btn btn-link p-0 m-0 text-decoration-none"
                                style={{ color: "#2563eb", lineHeight: 1, minWidth: "1.25rem" }}
                                aria-label={`Remove ${em}`}
                                onClick={() =>
                                  setRemovalNotifyManualChips((prev) =>
                                    prev.filter((x) => normalizeEmailKey(x) !== normalizeEmailKey(em))
                                  )
                                }
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton"
                    disabled={removalNotifySaving}
                    onClick={() => {
                      void handleSaveNotifySettings("removal");
                    }}
                  >
                    {removalNotifySaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              <div
                className="p-3 mt-4"
                style={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium text-dark">Shipping handling charges</span>
                    <Info size={16} color="#9CA3AF" />
                  </div>
                  <p
                    className="mb-0 mt-1"
                    style={{
                      fontSize: "15px",
                      color: "#6B7280",
                    }}
                  >
                    The amount you enter here is added to UPS shipping rates when orders are quoted and calculated at checkout.
                  </p>

                  <div className="mt-3">
                    <label
                      className="form-label mb-1 fw-medium text-dark"
                      htmlFor="shipping-handling-charges-input"
                      style={{ fontSize: "14px" }}
                    >
                      Handling charge amount
                    </label>
                    <input
                      id="shipping-handling-charges-input"
                      type="number"
                      min={0}
                      step="0.01"
                      max={99999999}
                      className="form-control"
                      placeholder="0.00"
                      value={shippingHandlingCharges}
                      disabled={shippingHandlingLoading || shippingHandlingSaving}
                      onChange={(e) => setShippingHandlingCharges(e.target.value)}
                      style={{
                        width: "14ch",
                        maxWidth: "180px",
                        minHeight: "39px",
                        borderRadius: "10px",
                        backgroundColor: "#f5f6fa",
                        border: "2px solid #c9ced6",
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton"
                    disabled={shippingHandlingSaving || shippingHandlingLoading}
                    onClick={() => {
                      void handleSaveShippingHandlingCharges();
                    }}
                  >
                    {shippingHandlingSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  </div>
);
};

export default SignageGeneralSettings;
